<template>
  <div class="corporate-hero-container">
    <div class="corporate-hero-background" :class="isGeneric && 'generic'">
      <div class="corporate-hero-content" :class="isGeneric && 'generic'">
        <h1 class="mb-16 text-h1">Use your free Sittercity membership</h1>
        <p id="descriptionText" class="mb-16 leading-normal text-grey-body">{{ descriptionText }}</p>
        <SearchComboBox
          v-if="isGeneric"
          id="companySearchBox"
          ref="companySearchBox"
          name="company-search"
          :results="clientSearch"
          :value="selectedCompanyId"
          type="text"
          labelName="Your employer's name"
          class="mb-16"
          :status="status"
          :errorText="missingEmployerError"
          @selected="updateValue"
          @changingInput="resetErrorState"
          @errorStateChanged="onErrorStateChanged"
          @valueUpdated="queryFragment"
        />
        <Button
          id="mainSignUpCTA"
          value="Sign up for free"
          class="w-full"
          size="medium"
          variant="primary"
          @click="queryGetClientSlug"
        />
        <div v-if="isGeneric" class="social-proof-section">
          <p class="text-caption">Featured in</p>
          <img
            class="social-proof-img"
            src="@/images/bh-benefits/company-logos.png"
            alt="Forbes, The New York Times, Scary Mommy, Good Morning America"
          />
        </div>
      </div>
      <img
        class="corporate-hero-photo"
        src="@/images/bh-benefits/corporate-hero-photo.svg"
        alt="A little girl riding on a sitter's back"
      />
    </div>
    <div class="care-type-container">
      <Card
        v-for="careType in careTypes"
        :key="careType.title"
        :elevation="4"
        class="care-type-card"
        :class="careType.title === 'Companion care' ? 'companion-care-card' : ''"
      >
        <BabysitterIcon v-if="careType.title === 'Child care'" class="mx-auto" />
        <HousekeepingIcon v-if="careType.title === 'Housekeeping'" class="mx-auto" />
        <DashboardPetCareIcon v-if="careType.title === 'Pet care'" class="mx-auto" />
        <CompanionCareIcon v-if="careType.title === 'Companion care'" class="mx-auto" />
        <h3>{{ careType.title }}</h3>
        <p class="text-grey-body">{{ careType.text }}</p>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import SearchComboBox from '../../../components/search_combo_box/SearchComboBox.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import BabysitterIcon from '@/icons/BabysitterIcon.vue';
import HousekeepingIcon from '@/icons/HousekeepingIcon.vue';
import DashboardPetCareIcon from '@/icons/DashboardPetCareIcon.vue';
import CompanionCareIcon from '@/icons/CompanionCareIcon.vue';
import { CLIENT_SEARCH_QUERY, CORPORATE_CLIENT_QUERY } from '../../../graphql/bh_benefits/queries';
import BHBenefitRoutes from '@/lib/constants/bh_benefit_route_names';
import { sendSegmentEvent } from '../../../lib/segment';
import helperStatus from '@/helpers/status';

export default {
  name: 'CorporateHero',
  components: {
    SearchComboBox,
    Button,
    Card,
    BabysitterIcon,
    HousekeepingIcon,
    DashboardPetCareIcon,
    CompanionCareIcon,
  },
  props: {
    slug: String,
    corporateClientName: String,
  },
  data(): any {
    return {
      selectedCompanyId: '',
      selectedCompanySlug: '',
      careTypes: [
        { title: 'Child care', text: 'Get in-home support from experienced nannies and babysitters' },
        { title: 'Housekeeping', text: 'Find cleaning, maintenance, and organization help for your home' },
        { title: 'Pet care', text: 'Get help with feeding, walks, and specialized care for any family pet' },
        { title: 'Companion care', text: 'Find help for aging adults with everyday activities or tasks around the home' },
      ],
      clientSearch: [],
      status: helperStatus.Neutral,
      missingEmployerError:
        'You may not be eligible for Premium through employer benefits, please <a href="/registration?member_type=seeker" class="underline">sign up here</a>',
    };
  },
  computed: {
    isGeneric(): Boolean {
      return !this.corporateClientName;
    },
    descriptionText(): String {
      const companySpecific = !this.isGeneric ? `${this.corporateClientName} is here to support your needs. ` : '';
      return `${companySpecific}Sign up to activate your free membership ($180/year\xa0value) and connect directly with care near you`;
    },
  },
  methods: {
    onErrorStateChanged(hasError) {
      if (hasError) {
        this.status = helperStatus.Error;
        this.invalidEmployerEntered();
      }
    },
    invalidEmployerEntered() {
      const date = new Date().toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' });
      const time = new Date().toLocaleTimeString('default', { hour: 'numeric', minute: 'numeric', second: 'numeric' });
      sendSegmentEvent('Invalid Employer Entered', {
        click_date: date,
        time_stamp: time,
      });
    },
    signUpClick(): void {
      if (!this.isGeneric) {
        this.$router.push({ name: BHBenefitRoutes.SIGNUP, params: { slug: this.slug } });
      }
      if (this.isGeneric && this.selectedCompanyId) {
        this.$router.push({ name: BHBenefitRoutes.SIGNUP, params: { slug: this.selectedCompanyId } });
      }
      if (this.isGeneric && !this.selectedCompanyId) {
        this.$refs.companySearchBox.$el.children[0].focus();
      }
    },
    updateValue(v): void {
      this.selectedCompanyId = v.value;
    },
    goToSignUpPage(slug: string): void {
      if (this.isGeneric) {
        if (this.selectedCompanyId) {
          if (this.$route.query.leadid) {
            this.$router.push({
              name: BHBenefitRoutes.SIGNUP,
              params: { slug },
              query: { leadid: this.$route.query.leadid },
            });
          } else {
            this.$router.push({ name: BHBenefitRoutes.SIGNUP, params: { slug } });
          }
        } else {
          this.status = helperStatus.Error;
        }
      } else {
        this.$router.push({ name: BHBenefitRoutes.SIGNUP, params: { slug: this.slug } });
      }
    },
    resetErrorState(): void {
      this.status = helperStatus.Neutral;
    },
    async queryGetClientSlug() {
      await this.$apollo
        .query({
          query: CORPORATE_CLIENT_QUERY,
          variables: {
            bhClientId: Number(this.selectedCompanyId),
          },
        })
        .then(({ data }) => {
          const { slug } = data.corporateClient;
          this.goToSignUpPage(slug);
        })
        .catch((error) => {
          this.status = helperStatus.Error;
          // eslint-disable-next-line no-console
          console.warn('GraphQL query error:', error);
        });
    },
    async queryFragment(fragment: string = '') {
      if (fragment.length < 3) return;
      await this.$apollo
        .query({
          query: CLIENT_SEARCH_QUERY,
          variables: {
            fragment,
          },
        })
        .then(({ data }) => {
          const corporateClientsForComboBox = data.clientSearch.map(({ clientId, clientName }) => {
            return { display: clientName, value: `${clientId}` };
          });
          this.clientSearch = corporateClientsForComboBox;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('GraphQL query error:', error);
        });
    },
  },
};
</script>

<style scoped>
h1 {
  font-size: 3rem;
}
</style>
